import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from "react-router-dom";
import Home from "./pages/Home";
import OurServices from "./pages/OurServices";
import AboutUs from "./pages/AboutUs";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import HowItWorks from "./pages/HowItWorks";
import Pricing from "./pages/Pricing";
import Apis from "./pages/ApiDocs/Apis";
import ForgotPassword from "./pages/ForgotPassword";
import Inquiry from "./pages/Inquiry";
import TermsConditions from "./pages/TermsConditions";
import Faq from "./pages/Faq";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import OurStory from "./pages/OurStory";
import ContactUs from "./pages/ContactUs";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import Dashboard from "./pages/auth/Dashboard";
import Apps from "./pages/auth/Apps";
import ChangePassword from "./pages/auth/ChangePassword";
import CustomerSupport from "./pages/auth/CustomerSupport";
import ManageCryptoCurriencies from "./pages/auth/ManageCryptoCurriencies";
import MyProfile from "./pages/auth/MyProfile";
import Transactions from "./pages/auth/Transactions";
import DepositHistory from "./pages/auth/PaymentLinks";
import WithdrawlHistory from "./pages/auth/WalletTransactions";
import PrivateRoute from "./pages/private/PrivateRoute";
import HasToken from "./pages/private/HasToken";
import AppsDetail from "./pages/auth/AppsDetail";
import ConversionHistory from "./pages/auth/ConversionHistory";
import DepositReceive from "./pages/auth/DepositReceive";
import Pay from "./pages/auth/Pay";
import PaymentInformation from "./pages/auth/PaymentInformation";
import SendWithdraw from "./pages/auth/SendWithdraw";
import Settings from "./pages/auth/Settings";
import OTP from "./pages/OTPVerification";
import WithdrawInvoice from "./pages/auth/WithdrawInvoice";
import Notification from "./pages/auth/Notification";
import TransactionView from "./pages/auth/TransactionView";
import SendWithdrawFiat from "./pages/auth/SendWithdrawFiat";
import FiatTransactions from "./pages/auth/FiatTransactions";
import ExtensionPrivacyPolicy from "./pages/ExtensionPrivacyPolicy";
import UserWithdrawals from "./pages/auth/UserWithdrawals";
import WithdrawalSettings from "./pages/auth/WithdrawalSettings";
import WebhookSettings from "./pages/auth/WebhookSettings";
import { AppSelectionProvider } from "./context/AppSelectionContext";

const HashDirectHandler = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (window.location.hash && window.location.hash.startsWith("#section-")) {
      const hash = window.location.hash;
      console.log("Redirecting hash from root to apis", hash);
      // Remove hash from current root to prevent infinite loop or buggy history
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      // Navigate to /apis with the hash
      navigate(`/apis${hash}`);
    }
  }, [navigate]);
  return null;
};

// Main domains that should show the FULL site.
// Any other domain (e.g. bitcryptogateway.xyz) is treated as a payment-only gateway.
const MAIN_DOMAINS = ['paycoinz.com', 'www.paycoinz.com', 'test.paycoinz.com', 'localhost'];

const App = () => {
  const isTestnet = process.env.REACT_APP_NETWORK_MODE === 'testnet';
  const currentHost = window.location.hostname;
  const isPaymentDomain = !MAIN_DOMAINS.includes(currentHost);

  return (
    <>
      <ToastContainer />
      <AppSelectionProvider>
        <Router>
          <HashDirectHandler />
          <Routes>
            {isPaymentDomain ? (
              <>
                {/* RESTRICTED MODE: Custom Payment Domain - Only allow Payment page */}
                <Route
                  path="/payment-information/:paymentId"
                  element={<PaymentInformation />}
                />
                
                {/* Catch-all: show 404 for restricted domains (hides main brand) */}
                <Route
                  path="*"
                  element={<div style={{display:'flex',justifyContent:'center',alignItems:'center',height:'100vh',fontFamily:'sans-serif',color:'#666'}}>404 - Not Found</div>}
                />
              </>
            ) : (
              <>
                {/* FULL SITE MODE: Regular Paycoinz platform */}
            {/* Public marketing pages — hidden on testnet */}
            {isTestnet ? (
              <>
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/our-services" element={<Navigate to="/login" replace />} />
                <Route path="/about-us" element={<Navigate to="/login" replace />} />
                <Route path="/how-it-works" element={<Navigate to="/login" replace />} />
                <Route path="/pricing" element={<Navigate to="/login" replace />} />
                <Route path="/inquire-now" element={<Navigate to="/login" replace />} />
                <Route path="/our-story" element={<Navigate to="/login" replace />} />
                <Route path="/contact-us" element={<Navigate to="/login" replace />} />
                <Route path="/faq" element={<Navigate to="/login" replace />} />
              </>
            ) : (
              <>
                <Route path="/" element={<Home />} />
                <Route path="/our-services" element={<OurServices />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/inquire-now" element={<HasToken><Inquiry /></HasToken>} />
                <Route path="/our-story" element={<OurStory />} />
                <Route path="/contact-us" element={<ContactUs />} />
                <Route path="/faq" element={<Faq />} />
              </>
            )}

            {/* Auth pages — always available */}
            <Route path="/login" element={<HasToken><Login /></HasToken>} />
            <Route path="/verify-otp" element={<OTP />} />
            <Route path="/terms-conditions" element={<TermsConditions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/extension-privacy-policy" element={<ExtensionPrivacyPolicy />} />
            <Route path="/reset-password" element={<HasToken><ResetPassword /></HasToken>} />
            <Route path="/forget-password" element={<HasToken><ForgotPassword /></HasToken>} />

            <Route
              path="/payment-information/:paymentId"
              element={<PaymentInformation />}
            />

            <Route
              path="/transactions-view/:transactionId"
              element={<TransactionView />}
            />

            {/* private routes */}

            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/apps"
              element={
                <PrivateRoute>
                  <Apps />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps-detail/:appId"
              element={
                <PrivateRoute>
                  <AppsDetail />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps-payment-links/:appId"
              element={
                <PrivateRoute>
                  <DepositHistory />
                </PrivateRoute>
              }
            />
            <Route
              path="/apps-wallet-transactions/:appId"
              element={
                <PrivateRoute>
                  <WithdrawlHistory />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps-conversion-history"
              element={
                <PrivateRoute>
                  <ConversionHistory />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps-deposit-receive/:appId"
              element={
                <PrivateRoute>
                  <DepositReceive />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps-send-withdraw/:appId"
              element={
                <PrivateRoute>
                  <SendWithdraw />
                </PrivateRoute>
              }
            />

            <Route
              path="/apps-send-withdraw-fiat/:appId"
              element={
                <PrivateRoute>
                  <SendWithdrawFiat />
                </PrivateRoute>
              }
            />

            <Route
              path="/change-password"
              element={
                <PrivateRoute>
                  <ChangePassword />
                </PrivateRoute>
              }
            />
            <Route
              path="/customer-support"
              element={
                <PrivateRoute>
                  <CustomerSupport />
                </PrivateRoute>
              }
            />

            <Route
              path="/dashboard-manage-curriencies"
              element={
                <PrivateRoute>
                  <ManageCryptoCurriencies />
                </PrivateRoute>
              }
            />
            <Route
              path="/my-profile"
              element={
                <PrivateRoute>
                  <MyProfile />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <PrivateRoute>
                  <Transactions />
                </PrivateRoute>
              }
            />

            <Route
              path="/fiat-transactions"
              element={
                <PrivateRoute>
                  <FiatTransactions />
                </PrivateRoute>
              }
            />

            <Route
              path="/withdraw-invoice"
              element={
                <PrivateRoute>
                  <WithdrawInvoice />
                </PrivateRoute>
              }
            />

            <Route
              path="/pay"
              element={
                <PrivateRoute>
                  <Pay />
                </PrivateRoute>
              }
            />

            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <Notification />
                </PrivateRoute>
              }
            />

            <Route
              path="/user-withdrawals"
              element={
                <PrivateRoute>
                  <UserWithdrawals />
                </PrivateRoute>
              }
            />

            <Route
              path="/withdrawal-settings"
              element={
                <PrivateRoute>
                  <WithdrawalSettings />
                </PrivateRoute>
              }
            />

            <Route
              path="/webhook-settings"
              element={
                <PrivateRoute>
                  <WebhookSettings />
                </PrivateRoute>
              }
            />

            {/* <Route
            path="/payment-information/:paymentId"
            element={
              <PrivateRoute>
                <PaymentInformation />
              </PrivateRoute>
            }
          /> */}
            <Route
              path="/apis"
              element={
                <PrivateRoute>
                  <Apis />
                </PrivateRoute>
              }
            />
          </>
        )}
      </Routes>
        </Router>
      </AppSelectionProvider>
    </>
  );
};

export default App;
